import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faSpinner, 
  faArchive,
  faUndo,
  faUser,
  faCalendarAlt,
  faUserTag,
  faExclamationTriangle,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';
import ConfirmationModal from '../../components/ConfirmationModal';

const ArchiveSupervisors = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [archivedSupervisors, setArchivedSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [supervisorToRestore, setSupervisorToRestore] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    console.log('ArchiveSupervisors component mounted, current user:', currentUser);
    fetchArchivedSupervisors();
  }, []);

  const fetchArchivedSupervisors = async () => {
    try {
      setLoading(true);
      console.log('Fetching archived supervisors from:', '/supervisors/archived');
      console.log('Auth token:', localStorage.getItem('auth_token'));
      console.log('Session token:', sessionStorage.getItem('auth_token'));
      const response = await api.get('/supervisors/archived');
      console.log('Full response object:', response);
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      
      if (response.data.success) {
        console.log('Success! Setting archived supervisors:', response.data.data);
        setArchivedSupervisors(response.data.data);
        setError(''); // Clear any previous errors
      } else {
        console.log('API returned success: false');
        setError(response.data.message || 'Erreur lors du chargement des prestataires archivés');
      }
    } catch (error) {
      console.error('Error fetching archived supervisors:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message ||
                          'Erreur lors du chargement des prestataires archivés';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreClick = (supervisor) => {
    setSupervisorToRestore(supervisor);
    setShowConfirmModal(true);
  };

  const handleRestore = async () => {
    if (!supervisorToRestore) return;

    try {
      setRestoring(supervisorToRestore.id);
      console.log('Restoring supervisor with ID:', supervisorToRestore.id);
      // `source` tells the API which table this row came from: a soft-archived supervisor is
      // restored in place, while a legacy row is re-created from the archive copy.
      const response = await api.post(`/supervisors/archived/${supervisorToRestore.id}/restore`, {
        source: supervisorToRestore.source || 'supervisor',
      });
      console.log('Restore response:', response.data);
      
      setSuccessMessage(`${supervisorToRestore.name} a été restauré avec succès`);
      setShowConfirmModal(false);
      setSupervisorToRestore(null);
      fetchArchivedSupervisors(); // Refresh the list
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      console.error('Error restoring supervisor:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la restauration du prestataire';
      setError(errorMessage);
    } finally {
      setRestoring(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'suspended': return 'text-yellow-600 bg-yellow-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'suspended': return 'Suspendu';
      case 'inactive': return 'Inactif';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <FontAwesomeIcon 
              icon={faSpinner} 
              className="animate-spin text-red-600 text-4xl mb-4" 
            />
            <p className="text-gray-600 text-lg">Chargement des prestataires archivés...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-600 text-lg mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-red-100 rounded-xl">
                <FontAwesomeIcon icon={faArchive} className="text-red-600 text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Prestataires Archivés</h1>
                <p className="text-gray-600 mt-1">
                  Liste des prestataires archivés - {archivedSupervisors.length} prestataire(s)
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 mr-2" />
              {error}
            </div>
          </div>
        )}

        {/* Archived Supervisors Table */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Référence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Archivé le
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Archivé par
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Raison d'archivage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/50 divide-y divide-gray-200">
                {archivedSupervisors.map((supervisor) => (
                  <tr key={supervisor.id} className="hover:bg-gray-100/80 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {supervisor.ref}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {supervisor.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {supervisor.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(supervisor.status)}`}>
                        {getStatusText(supervisor.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 mr-2" />
                        {new Date(supervisor.archived_at).toLocaleDateString('fr-FR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faUserTag} className="text-gray-400 mr-2" />
                        {supervisor.archived_by_name || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-400 mr-2" />
                        {supervisor.archive_reason || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleRestoreClick(supervisor)}
                        disabled={restoring === supervisor.id}
                        className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                      >
                        {restoring === supervisor.id ? (
                          <FontAwesomeIcon icon={faSpinner} className="animate-spin w-4 h-4" />
                        ) : (
                          <FontAwesomeIcon icon={faUndo} className="w-4 h-4" />
                        )}
                        <span>Restaurer</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {archivedSupervisors.length === 0 && (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faArchive} className="text-gray-400 text-4xl mb-4" />
              <p className="text-gray-500 text-lg">Aucun prestataire archivé</p>
              <p className="text-gray-400 text-sm mt-2">
                Les prestataires archivés apparaîtront ici
              </p>
            </div>
          )}
        </div>

        {/* Archive Information */}
        {archivedSupervisors.length > 0 && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-blue-500 mt-1 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-blue-800">Information sur les archives</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Les prestataires archivés conservent toutes leurs informations et peuvent être restaurés à tout moment.
                  La restauration créera un nouveau prestataire avec les mêmes données.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setSupervisorToRestore(null);
        }}
        onConfirm={handleRestore}
        title="Restaurer le prestataire"
        message={`Êtes-vous sûr de vouloir restaurer le prestataire "${supervisorToRestore?.name}" ? Il redeviendra actif et sera supprimé de cette liste d'archives.`}
        confirmText="Restaurer"
        cancelText="Annuler"
        confirmColor="bg-red-600 hover:bg-red-700"
        loading={restoring === supervisorToRestore?.id}
      />
    </div>
  );
};

export default ArchiveSupervisors;
